'use client';
// Diffusion & accusés (design isDiff) — taux global, axes « par document » /
// « par lecteur », relances, émission de diffusion (RQ).
import React, { useState } from 'react';
import { useGed, userName, userInitials } from '@/lib/store';
import { fmtDate } from '@/lib/meta';
import { useRemonteEnHaut, TitrePage, I, card, inputStyle, Skeleton, EmptyState, useBusy, ChampRecherche, enChargement as calculerChargement } from '@/components/ui';
import { Norme } from '@/components/Norme';

import { Bouton } from '@/shared/Bouton';
import { Segment } from '@/shared/Segment';
export function Diffusion() {
  const { docs, settings, session, act, openDoc, loading, booted } = useGed();
  const isRQ = session.roles.includes('rq');
  const [axis, setAxis] = useState<'doc' | 'reader'>('doc');
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(axis);
  const [q, setQ] = useState('');
  const [diffuseFor, setDiffuseFor] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const { run, estOccupe } = useBusy();
  /* ⚠️ `settings` COMPTE AUTANT QUE `docs` ici. Le tableau des lecteurs se
     construit sur `settings.gedUsers` : quand les documents arrivaient avant le
     paramétrage, la garde tombait, et l'écran affirmait « aucun lecteur »
     — c'est-à-dire qu'aucun document n'est diffusé à personne. Une garde qui ne
     surveille qu'une des deux sources ne protège que la moitié de l'écran. */
  const enChargement = calculerChargement(booted, loading, docs.length, settings?.gedUsers?.length || 0);

  const applicable = docs.filter((d) => ['envigueur', 'enrevision', 'retire'].includes(d.statut) && (d.diffusion || []).length);
  const allEntries = applicable.flatMap((d) => (d.diffusion || []).map((x) => ({ doc: d, e: x })));
  const req = allEntries.filter((x) => x.e.requis);
  // Sans accusé requis, il n'y a pas de taux : afficher « 100 % » en vert
  // annonçait une conformité parfaite alors qu'on ne mesurait rien — et c'était
  // l'information la plus visible de l'écran pendant tout le chargement.
  const global = req.length ? Math.round((req.filter((x) => x.e.dateAck).length / req.length) * 100) : null;

  const qq = q.trim().toLowerCase();
  const docRows = applicable.filter((d) => !qq || `${d.code} ${d.titre}`.toLowerCase().includes(qq));
  const readers = (settings?.gedUsers || []).filter((u) => u.roles.includes('lecteur') || allEntries.some((x) => x.e.userId === u.id));
  const readerRows = readers
    .map((u) => ({ u, items: allEntries.filter((x) => x.e.userId === u.id) }))
    .filter((r) => r.items.length && (!qq || r.u.nom.toLowerCase().includes(qq)));

  const envigueur = docs.filter((d) => d.statut === 'envigueur');

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <TitrePage eyebrow={<>Opposabilité aux opérateurs<Norme art="7.3.6" /></>} titre="Diffusion & accusés de lecture"
            sousTitre="Un document n’est opposable à un opérateur qu’après accusé de lecture de la version précise qui lui est diffusée." />
        </div>
        <div style={{ ...card, padding: '14px 22px', textAlign: 'center', minWidth: 168 }}>
          {enChargement ? (
            <span className="ged-skel-bar" style={{ display: 'block', width: 74, height: 26, borderRadius: 'var(--radius-xs)', margin: '0 auto' }} />
          ) : (
            /* ⚠️ UNE TUILE DE MESURE NE PORTE PAS UNE PHRASE (relevé DAT-12 du
               26/08/2026 : « — » au-dessus de « AUCUN ACCUSÉ REQUIS »).
               Le tiret occupait la place du chiffre, dans la graisse et la
               taille d'un chiffre, et l'explication passait en libellé : on lit
               d'abord un caractère qui ne veut rien dire, puis une phrase là où
               l'œil attend le nom de la mesure. La tuile disait donc deux fois
               « rien », et jamais QUOI.
               ⚠️ On ne met surtout pas « 0 % » : c'est le principe que porte
               déjà l'écran des indicateurs — un taux calculé sur zéro n'est pas
               nul, il n'existe pas. On garde le libellé à sa place et on écrit
               l'absence en toutes lettres, en petit. */
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: global === null ? 13 : 30, fontWeight: 600, lineHeight: global === null ? 1.35 : 1, paddingBlock: global === null ? 5 : 0, color: global === null ? 'var(--text-muted)' : global >= 90 ? 'var(--green-600)' : global >= 70 ? 'var(--amber-600)' : 'var(--red-600)' }}>
              {global === null ? 'rien à mesurer' : `${global} %`}
            </div>
          )}
          <div style={{ fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 6 }}>
            {!enChargement && global === null ? 'Aucun accusé n’est requis' : 'Taux d’accusé global'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <Segment ariaLabel="Regroupement" valeur={axis} onChange={setAxis}
          options={[{ valeur: 'doc' as const, libelle: 'Par document' }, { valeur: 'reader' as const, libelle: 'Par lecteur' }]} />
        <ChampRecherche valeur={q} onChange={setQ} placeholder="Rechercher…" style={{ ...inputStyle, flex: '1 1 200px', maxWidth: 320 }} />
      </div>

      {axis === 'doc' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {enChargement && <div style={card}><Skeleton rows={3} height={62} /></div>}
          {!enChargement && docRows.length === 0 && (
            <div style={card}>
              <EmptyState
                titre={qq ? 'Aucun document ne correspond à cette recherche.' : 'Aucune diffusion émise.'}
                indice={qq ? 'Essayez un autre code ou un autre titre.' : 'Utilisez « Émettre une diffusion » ci-dessous pour désigner les lecteurs d’un document en vigueur.'} />
            </div>
          )}
          {docRows.map((d) => {
            const reqE = (d.diffusion || []).filter((x) => x.requis);
            const okE = reqE.filter((x) => x.dateAck).length;
            const pend = reqE.length - okE;
            const relTotal = reqE.reduce((s, x) => s + (x.relances || 0), 0);
            return (
              <div key={d.documentId} style={{ ...card, padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <button onClick={() => openDoc(d.code, 'diffusion')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 700, color: 'var(--color-primary-active)', padding: 0 }}>{d.code}</button>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-strong)', flex: 1 }}>{d.titre}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, color: 'var(--text-muted)' }}>v{d.versionCourante}</span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: pend ? 'var(--amber-600)' : 'var(--green-600)' }}>{okE}/{reqE.length} accusés</span>
                  {relTotal > 0 && <span title="Relances tracées sur les lecteurs en attente d’accusé" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 4 }}><I n="mail" s={12} />{relTotal} relance{relTotal > 1 ? 's' : ''}</span>}
                  {isRQ && pend > 0 && (
                    // La route n'est pas idempotente : deux clics comptent DEUX
                    // relances sur les mêmes lecteurs. Le bouton reste donc
                    // immobilisé jusqu'au retour du serveur.
                    <Bouton taille="sm" onClick={() => void run(`rel-${d.documentId}`, () => act(d.documentId, 'relance'))} disabled={estOccupe()}>
                      <I n="mail" s={13} />{estOccupe(`rel-${d.documentId}`) ? 'Envoi…' : `Relancer (${pend})`}
                    </Bouton>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {(d.diffusion || []).map((x, i) => (
                    <span key={`${x.userId}-${i}`} title={x.dateAck ? `Lu le ${fmtDate(x.dateAck)}` : x.requis ? `En attente d’accusé${x.relances ? ` — ${x.relances} relance(s) envoyée(s)` : ''}` : 'Pour information'}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-pill)', padding: '3px 10px', background: x.dateAck ? 'var(--green-50)' : x.requis ? 'var(--amber-50)' : 'var(--gray-50)', color: x.dateAck ? 'var(--green-600)' : x.requis ? 'var(--amber-600)' : 'var(--text-muted)' }}>
                      {userInitials(settings, x.userId)} {x.dateAck ? '✓' : x.requis ? '…' : 'ℹ'}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {axis === 'reader' && (
        <div style={{ display: 'grid', gap: 12 }}>
          {enChargement && <div style={card}><Skeleton rows={3} height={62} /></div>}
          {!enChargement && readerRows.length === 0 && (
            <div style={card}>
              <EmptyState
                titre={qq ? 'Aucun lecteur ne correspond à cette recherche.' : 'Aucun lecteur avec diffusion.'}
                indice={qq ? 'La recherche porte sur le nom du lecteur.' : undefined} />
            </div>
          )}
          {readerRows.map(({ u, items }) => {
            const pend = items.filter((x) => x.e.requis && !x.e.dateAck).length;
            return (
              <div key={u.id} style={{ ...card, padding: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--cyan-100)', color: 'var(--cyan-700)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700 }}>{u.initiales}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>{u.nom}</span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{u.fonction}</span>
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: pend ? 'var(--amber-600)' : 'var(--green-600)' }}>{pend ? `${pend} en attente` : 'À jour'}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                  {items.map(({ doc, e }) => (
                    <button key={doc.code} onClick={() => openDoc(doc.code, 'diffusion')} title={e.dateAck ? `Lu le ${fmtDate(e.dateAck)}` : 'En attente'}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, fontFamily: 'var(--font-mono)', borderRadius: 'var(--radius-pill)', padding: '3px 10px', border: 'none', cursor: 'pointer', background: e.dateAck ? 'var(--green-50)' : 'var(--amber-50)', color: e.dateAck ? 'var(--green-600)' : 'var(--amber-600)' }}>
                      {doc.code} {e.dateAck ? '✓' : '…'}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isRQ && (() => {
        /* ── GED-3, 07/08/2026 : cet écran se comporte comme DocDetail ────────
           Il ne faisait NI pré-cocher NI exclure : il proposait tout le monde,
           sans distinguer les personnes déjà servies. Ce n'était pas dangereux
           — le serveur fusionne, un déjà-servi coché est un no-op — c'était
           MENSONGER : « Diffuser (12) » annonçait douze envois là où trois
           lignes seulement allaient être créées, sur l'écran dont la fonction
           entière est de tenir un compte juste.
           La règle retenue est celle de DocDetail : tout le monde affiché,
           les déjà servis marqués et non cochables, et le compte ne compte que
           les ajouts. « Déjà servi » est un état d'une LIGNE de la diffusion de
           ce document — d'où la mention par personne ET par document, jamais un
           filtrage global. */
        const docChoisi = envigueur.find((d) => d.documentId === diffuseFor) || null;
        const dejaSur = new Map((docChoisi?.diffusion || []).map((x) => [x.userId, x]));
        const tous = settings?.gedUsers || [];
        const choisis = Object.keys(picked).filter((k) => picked[k] && !dejaSur.has(k));
        return (
          <div style={{ ...card, padding: 18 }}>
            <h3 style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-strong)', margin: '0 0 10px' }}>Émettre une diffusion</h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <select value={diffuseFor || ''} onChange={(e) => { setDiffuseFor(e.target.value || null); setPicked({}); }} style={{ ...inputStyle, width: 'auto', minWidth: 260 }}>
                <option value="">Choisir un document en vigueur…</option>
                {envigueur.map((d) => <option key={d.documentId} value={d.documentId}>{d.code} — {d.titre}</option>)}
              </select>
              {docChoisi && (
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {dejaSur.size} déjà destinataire(s) sur {tous.length} · {tous.length - dejaSur.size} à ajouter
                </span>
              )}
            </div>
            {docChoisi && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
                {tous.map((u) => {
                  const e = dejaSur.get(u.id);
                  const lu = !!e?.dateAck;
                  return (
                    <label key={u.id} title={e
                      ? (lu ? `Accusé de lecture donné le ${fmtDate(e.dateAck)}.`
                        : e.requis ? `En attente d’accusé${e.echeance ? `, échéance au ${fmtDate(e.echeance)}` : ''}. Le rediffuser ne repousserait pas cette échéance : le serveur fusionne, il ne réémet pas.`
                          : 'Destinataire pour information : aucun accusé n’est exigé de lui.')
                      : undefined}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                        background: e ? (lu ? 'var(--green-50)' : 'var(--amber-50)') : picked[u.id] ? 'var(--cyan-50)' : 'var(--surface-sunken)',
                        border: `1px solid ${e ? 'transparent' : picked[u.id] ? 'var(--cyan-200)' : 'var(--border-default)'}`,
                        borderRadius: 'var(--radius-pill)', padding: '5px 12px',
                        cursor: e ? 'help' : 'pointer', opacity: e ? 0.8 : 1,
                        color: e ? (lu ? 'var(--green-600)' : 'var(--amber-600)') : 'inherit',
                      }}>
                      <input type="checkbox" disabled={!!e} checked={!!picked[u.id] && !e}
                        onChange={(ev) => setPicked({ ...picked, [u.id]: ev.target.checked })}
                        style={{ accentColor: 'var(--color-primary)' }} />
                      {u.nom}
                      {e && <span style={{ fontSize: 12.5, fontWeight: 600 }}>· déjà destinataire {lu ? '· lu' : e.requis ? '· en attente' : '· pour information'}</span>}
                    </label>
                  );
                })}
                {/* Le bouton sortait en silence quand aucun lecteur n'était coché :
                    il est désormais désactivé, et dit pourquoi. */}
                <Bouton variante="primaire" taille="sm" onClick={() => void run('diffuse', async () => {
                    const ok = await act(docChoisi.documentId, 'diffuse', { readers: choisis.map((userId) => ({ userId, requis: true })) });
                    if (ok) { setDiffuseFor(null); setPicked({}); }
                  })} disabled={!choisis.length || estOccupe()} title={!choisis.length ? 'Cocher au moins un lecteur qui n’est pas déjà destinataire' : undefined}>
                  <I n="send" s={13} />{estOccupe('diffuse') ? 'Envoi…' : `Ajouter ${choisis.length} destinataire${choisis.length > 1 ? 's' : ''}`}
                </Bouton>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
